import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useOrgUsers } from "../../../modules/admin/api/useOrgUsers";
import Card from "../../../components/core/basic/Card";
import { Tabs, TabList, Tab, TabPanel } from "../../../components/core/basic/Tabs";
import OrgUsersTable from "../../../modules/user_management/components/OrgUsersTable";
import UserInvitationTable from "../../../modules/user_management/components/UserInvitationTable";
import Loading from "../../../components/core/blocks/Loading";
import useQueryParams from "../../../hooks/useQueryParams";
import { useSimpleCan } from "../../../config/casl/casl";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";
import { UserInvitationModal } from "../../../modules/user_management/components/UserInvitationForm";
import { useAppSelector } from "../../../redux/hooks";

const UserManagement = () => {
  const { t } = useTranslation(["user_management"]);
  const [activeTab, setActiveTab] = useQueryParams("tab", "users");

  const user = useAppSelector((state) => state.user);

  const userCanReadUsers = useSimpleCan("read", "User");
  const userFilters: { include_staff: boolean; agency_id?: number } = {
    include_staff: false,
  };

  if (user.is_staff && user.account_manager.agency_id) {
    userFilters.agency_id = user.account_manager.agency_id;
  }

  const orgUsersQuery = useOrgUsers(userFilters);

  return (
    <PageContainer>
      {userCanReadUsers ?
        <VStack
          gap="medium"
          alignItems={"stretch"}
          alignSelf={"center"}
          width={"100%"}
          maxWidth={"1250px"}
          paddingY="large"
          paddingX={{ base: "medium", md: "xxlarge" }}
        >
          <h1 className={css({ fontSize: "xxlarge", fontWeight: "bold", textAlign: "center" })}>
            {t(($) => $.user_management["Manage Users"])}
          </h1>
          <Card>
            <Tabs selectedKey={activeTab ?? "users"} onSelectionChange={(key) => setActiveTab(key as string)}>
              <TabList>
                <Tab id="users">{t(($) => $.user_management.Users)}</Tab>
                <Tab id="invites">{t(($) => $.user_management.Invites)}</Tab>
              </TabList>
              <TabPanel id="users">
                {orgUsersQuery.isLoading ?
                  <Loading />
                : <>
                    <h2
                      className={css({
                        fontSize: "xlarge",
                        fontWeight: "bold",
                        textAlign: "center",
                      })}
                    >
                      {t(($) => $.user_management["Active Users"])}{" "}
                    </h2>
                    <OrgUsersTable
                      userData={orgUsersQuery.data?.filter((user) => user.is_active === true) ?? []}
                      isActiveUsersTable={true}
                    />
                    <h2 className={css({ fontSize: "xlarge", fontWeight: "bold", textAlign: "center" })}>
                      {t(($) => $.user_management["Inactive Users"])}
                    </h2>
                    <OrgUsersTable
                      userData={orgUsersQuery.data?.filter((user) => user.is_active === false) ?? []}
                      isActiveUsersTable={false}
                    />
                  </>
                }
              </TabPanel>
              <TabPanel id="invites">
                <Flex justifyContent={"center"}>
                  <UserInvitationModal />
                </Flex>
                <UserInvitationTable />
              </TabPanel>
            </Tabs>
          </Card>
        </VStack>
      : <UnauthorizedWarning />}
    </PageContainer>
  );
};

export const Route = createFileRoute("/_authenticated/user/usermanagement")({
  component: UserManagement,
  staticData: { application: "Admin" },
});
